<template>
  <themed-modal
    name="comment-edit-modal"
    title="Edit Comment"
    size="lg"
    :click-to-close="!isSaving"
    @opened="handleOpened"
    @closed="handleClosed"
  >
    <form @submit.prevent="handleSubmit" class="comment-edit-form">
      <!-- Comment Text -->
      <div class="form-group">
        <label for="edit-comment-text" class="form-label required">Comment</label>
        <textarea
          id="edit-comment-text"
          ref="commentInput"
          v-model="form.comment"
          class="form-textarea"
          rows="8"
          placeholder="Enter your comment here..."
          :disabled="isSaving"
          aria-required="true"
        ></textarea>
        <p v-if="errors.comment" class="form-error" role="alert">
          {{ errors.comment }}
        </p>
      </div>

      <!-- Attachment Display/Remove -->
      <div v-if="form.attachment" class="form-group">
        <label class="form-label">Current Attachment</label>
        <div class="comment-attachment-display">
          <div class="comment-attachment-display__icon" v-html="form.attachment.icon"></div>
          <div class="comment-attachment-display__info">
            <p class="comment-attachment-display__name">{{ form.attachment.name }}</p>
            <p v-if="form.attachment.description" class="comment-attachment-display__description">
              {{ form.attachment.description }}
            </p>
          </div>
          <div class="comment-attachment-display__actions">
            <themed-button
              type="button"
              variant="outline"
              size="sm"
              :href="form.attachment.view_url"
              target="_blank"
            >
              View
            </themed-button>
            <themed-button
              type="button"
              variant="outline"
              size="sm"
              :href="form.attachment.download_url"
            >
              Download
            </themed-button>
            <themed-button
              type="button"
              variant="destructive"
              size="sm"
              @click="removeAttachment"
              :disabled="isSaving"
            >
              Remove
            </themed-button>
          </div>
        </div>
      </div>

      <!-- Form Actions -->
      <div class="form-actions">
        <themed-button
          type="button"
          variant="outline"
          @click="handleCancel"
          :disabled="isSaving"
        >
          Cancel
        </themed-button>
        <themed-button
          type="submit"
          variant="default"
          :disabled="isSaving || !form.comment.trim()"
        >
          <svg
            v-if="isSaving"
            class="animate-spin w-4 h-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span>{{ isSaving ? 'Saving...' : 'Save Changes' }}</span>
        </themed-button>
      </div>
    </form>
  </themed-modal>
</template>

<script>
export default {
  name: 'CommentEditModal',
  
  props: {
    comment: {
      type: Object,
      default: null
    },
    documents: {
      type: Array,
      default: () => []
    }
  },
  
  data() {
    return {
      form: {
        id: null,
        comment: '',
        attachment_id: null,
        attachment: null
      },
      isSaving: false,
      errors: {},
      previouslyFocusedElement: null
    };
  },
  
  watch: {
    comment: {
      handler(newComment) {
        if (newComment) {
          this.populateForm(newComment);
        }
      },
      immediate: true
    }
  },
  
  methods: {
    /**
     * Handle modal opened
     */
    handleOpened() {
      this.previouslyFocusedElement = document.activeElement;
      
      if (this.comment) {
        this.populateForm(this.comment);
      }
      
      this.$nextTick(() => {
        if (this.$refs.commentInput) {
          this.$refs.commentInput.focus();
        }
      });
    },
    
    /**
     * Handle modal closed
     */
    handleClosed() {
      this.resetForm();
      
      if (this.previouslyFocusedElement) {
        this.$nextTick(() => {
          this.previouslyFocusedElement.focus();
        });
      }
    },
    
    /**
     * Populate form with comment data
     */
    populateForm(comment) {
      this.form = {
        id: comment.id,
        comment: comment.comment || '',
        attachment_id: comment.attachment_id || null,
        attachment: comment.attachment || null
      };
      this.errors = {};
    },
    
    /**
     * Remove attachment
     */
    removeAttachment() {
      this.form.attachment_id = null;
      this.form.attachment = null;
    },
    
    /**
     * Validate form
     */
    validateForm() {
      this.errors = {};
      
      if (!this.form.comment || !this.form.comment.trim()) {
        this.errors.comment = 'Comment is required';
        return false;
      }
      
      return true;
    },
    
    /**
     * Handle form submission
     */
    async handleSubmit() {
      if (!this.validateForm()) return;
      
      this.isSaving = true;
      
      try {
        const response = await window.axios.patch(`/comments/${this.form.id}`, {
          comment: this.form.comment,
          attachment_id: this.form.attachment_id
        });
        
        this.$emit('comment-updated', response.data.comment);
        
        this.$notify({
          type: 'success',
          title: 'Success',
          text: response.data.message || 'Comment updated successfully'
        });
        
        this.$modal.hide('comment-edit-modal');
      } catch (error) {
        console.error('Error updating comment:', error);
        
        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
        }
        
        this.$notify({
          type: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'Failed to update comment'
        });
      } finally {
        this.isSaving = false;
      }
    },
    
    /**
     * Handle cancel
     */
    handleCancel() {
      this.$modal.hide('comment-edit-modal');
    },
    
    /**
     * Reset form
     */
    resetForm() {
      this.form = {
        id: null,
        comment: '',
        attachment_id: null,
        attachment: null
      };
      this.errors = {};
      this.isSaving = false;
    }
  }
};
</script>
